import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { tg } from '../../lib/tg';

export type PeriodPreset = '7d' | '30d' | 'all' | 'custom';

interface Range {
  from?: string; // YYYY-MM-DD
  to?: string;
}

interface Props {
  label?: string;
  period: PeriodPreset;
  range: Range;
  onChange: (period: PeriodPreset, range: Range) => void;
}

/**
 * Period chips + an inline date-range picker for the "Custom" option.
 *
 * 7d / 30d / All — quick presets that compute a from-now cutoff.
 * Custom — opens a small panel with two `<input type="date">` fields.
 * Native picker is good enough on mobile (Telegram WebApp surfaces
 * the OS date wheel) and avoids shipping a calendar library.
 */
export function DateRangeRow({ label, period, range, onChange }: Props) {
  const { t } = useTranslation();
  const [openCustom, setOpenCustom] = useState(period === 'custom');

  const setPreset = (p: PeriodPreset) => {
    tg.hapticLight();
    if (p === 'custom') {
      setOpenCustom(true);
      onChange('custom', range);
    } else {
      setOpenCustom(false);
      onChange(p, {});
    }
  };

  const customLabel =
    period === 'custom' && (range.from || range.to)
      ? `${range.from ?? '…'} → ${range.to ?? '…'}`
      : t('activity.period.custom');

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        {label ? <span className="pb-eyebrow shrink-0">{label}</span> : null}
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <PresetChip
            active={period === '7d'}
            onClick={() => setPreset('7d')}
            label={t('activity.period.7d')}
          />
          <PresetChip
            active={period === '30d'}
            onClick={() => setPreset('30d')}
            label={t('activity.period.30d')}
          />
          <PresetChip
            active={period === 'all'}
            onClick={() => setPreset('all')}
            label={t('activity.period.all')}
          />
          <PresetChip
            active={period === 'custom'}
            onClick={() => setPreset('custom')}
            label={customLabel}
          />
        </div>
      </div>
      {openCustom ? (
        <div className="grid grid-cols-2 gap-2 rounded-pb-md border border-pb-border-soft bg-pb-paper2 p-2">
          <DateField
            label={t('activity.period.from')}
            value={range.from ?? ''}
            onChange={(v) => onChange('custom', { ...range, from: v || undefined })}
          />
          <DateField
            label={t('activity.period.to')}
            value={range.to ?? ''}
            onChange={(v) => onChange('custom', { ...range, to: v || undefined })}
          />
        </div>
      ) : null}
    </div>
  );
}

function PresetChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'pb-focus-ring rounded-pb-sm border px-2.5 py-1 text-[12px] font-medium transition-colors',
        active
          ? 'border-pb-accent bg-pb-accent text-white'
          : 'border-pb-border-soft bg-pb-paper text-tg-hint hover:text-tg-text',
      )}
    >
      {label}
    </button>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="pb-eyebrow !text-[10px] !tracking-[0.07em]">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-pb-sm border border-pb-border-soft bg-tg-bg px-2 py-1.5 text-[13px] text-tg-text focus:border-pb-accent-line focus:outline-none"
      />
    </label>
  );
}

/**
 * Convert a (period, range) pair into ISO-string from_ts / to_ts
 * suitable for the `/v1/activity` query params.
 *
 * `to_ts` is exclusive at the API; for "custom" with a `to` date we
 * push to end-of-day so the user's pick of e.g. 2026-05-06 includes
 * everything that day.
 */
export function periodToRange(
  period: PeriodPreset,
  range: Range,
): { from_ts?: string; to_ts?: string } {
  if (period === '7d')
    return { from_ts: new Date(Date.now() - 7 * 86400_000).toISOString() };
  if (period === '30d')
    return { from_ts: new Date(Date.now() - 30 * 86400_000).toISOString() };
  if (period === 'custom') {
    const out: { from_ts?: string; to_ts?: string } = {};
    // The native `<input type="date">` returns a calendar date in the
    // user's local timezone. Earlier we built the boundary as
    // `${date}T00:00:00Z` — that's UTC midnight, which is the previous
    // local day for west-of-UTC users and the next day for east-of-UTC
    // ones. Construct the boundary in LOCAL time so a user picking
    // "May 6" in Moscow gets the May-6-Moscow window, not the May-5
    // -London-evening one. (Codex P2.)
    if (range.from) out.from_ts = _localStartOfDay(range.from).toISOString();
    if (range.to) {
      const d = _localStartOfDay(range.to);
      // `to` is inclusive of the picked day; bump by one local day so
      // the API's exclusive `to_ts < created_at` matches.
      d.setDate(d.getDate() + 1);
      out.to_ts = d.toISOString();
    }
    return out;
  }
  return {};
}

function _localStartOfDay(yyyymmdd: string): Date {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  // `new Date(y, m-1, d)` constructs the date in the local timezone.
  // The resulting `.toISOString()` then converts that local-midnight
  // back into a correct UTC instant.
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}
